// Transaction service — RPC wrappers + the shared TransactionDao instance.
//
// The former "section read methods" layer (getTransactionDetail, getFiles,
// getEmailSends, …) was removed as dead code — every section it served now
// fetches through the framework (Refine useList / page-batched sub-queries).
// TBR: docs/test-breakage-reports/2026-07-21-remove-transactionservice-dead-read-layer.md

import { TransactionDao } from './TransactionDao.ts';
import type { SupabaseClient, InvalidateFn } from '../base.ts';

// ── TransactionService ──────────────────────────────────────────────────

export class TransactionService {
  readonly dao: TransactionDao;
  private readonly client: SupabaseClient;

  constructor(sb: SupabaseClient, invalidate?: InvalidateFn) {
    this.client = sb;
    this.dao = new TransactionDao(sb, invalidate);
  }

  /**
   * Resolve the sales employee for a given company/product/side combination.
   * Wraps the `resolve_sales_employee` RPC — routes through the service so
   * callers don't bypass the DAO boundary with raw supabaseClient.rpc calls.
   */
  async resolveSalesEmployee(
    companyId: number,
    productId: number | null,
    side: "load" | "unload",
  ): Promise<number | null> {
    const res = await this.client.rpc("resolve_sales_employee", {
      p_company_id: companyId,
      p_product_id: productId,
      p_side:       side,
    });
    return (res.data as number | null) ?? null;
  }

  /** Re-apply all active transaction rules to the given tx ids. Returns the
   *  number of rows the RPC reports as touched (falls back to ids.length). */
  async reapplyRulesToTransactions(transactionIds: number[]): Promise<number> {
    const res = await this.client.rpc("reapply_rule_to_transactions", {
      p_tx_ids: transactionIds,
    });
    if (res.error) throw res.error;
    return typeof res.data === "number" ? res.data : transactionIds.length;
  }
}
